import { getPortalSdk } from 'portal-sdk';
import { UseQueryOptions, useQuery } from 'react-query';

import {
  VoiceboxApp,
  VoiceboxMessage,
  VoiceboxRequestMessage,
  VoiceboxResponseAction,
  VoiceboxResponseMessage,
  VoiceboxResponseMessageType,
} from 'bones/constants/voicebox/message';

export const usePortalVoiceboxConversation = (
  conversationId: string,
  options?: UseQueryOptions<{
    conversationId: string;
    messages: VoiceboxMessage[] | null;
  }>
) =>
  useQuery<{
    conversationId: string;
    messages: VoiceboxMessage[] | null;
  }>(
    `usePortalVoiceboxConversation-${conversationId}`,
    async () => {
      if (!conversationId) {
        return { conversationId, messages: null };
      }

      const sdk = getPortalSdk();
      if (!sdk) {
        throw new Error('!sdk');
      }

      try {
        const converation = await sdk.getVoiceboxConversation(conversationId);
        if (!converation || !converation.id) {
          return { conversationId, messages: null };
        }

        const messages = converation.messages.reduce<VoiceboxMessage[]>(
          (acc, rawMessage) => {
            if (!rawMessage.id) {
              return acc;
            }
            const messageId = rawMessage.id;

            if (
              rawMessage.sender !== 'system' &&
              rawMessage.user_message_context &&
              rawMessage.user_message_context.app &&
              rawMessage.user_message_context.connection_id
            ) {
              const message: VoiceboxRequestMessage = {
                conversationId,
                messageId,
                app: rawMessage.user_message_context.app as VoiceboxApp,
                connectionId: rawMessage.user_message_context.connection_id,
                query: rawMessage.content || '',
                context: {},
                isRequest: true,
              };

              if (
                typeof rawMessage.user_message_context.database === 'string'
              ) {
                message.context.database_id =
                  rawMessage.user_message_context.database;
              }
              if (typeof rawMessage.user_message_context.model === 'string') {
                message.context.model = rawMessage.user_message_context.model;
              }
              if (rawMessage.user_message_context.named_graphs) {
                message.context.named_graphs =
                  rawMessage.user_message_context.named_graphs.filter(
                    (graph): graph is string => Boolean(graph)
                  );
              }
              if (
                typeof rawMessage.user_message_context.reasoning === 'boolean'
              ) {
                message.context.reasoning =
                  rawMessage.user_message_context.reasoning;
              }

              acc.push(message);
            } else if (
              rawMessage.sender === 'system' &&
              rawMessage.system_message_context
            ) {
              const message: VoiceboxResponseMessage = {
                conversationId,
                messageId,
                result: rawMessage.content || '',
                pending: rawMessage.system_message_context.pending || false,
                success: rawMessage.system_message_context.success || false,
                isResponse: true,
              };

              if (rawMessage.system_message_context.actions) {
                message.actions =
                  rawMessage.system_message_context.actions.filter(
                    (action): action is VoiceboxResponseAction =>
                      Boolean(action)
                  );
              }
              if (rawMessage.system_message_context.message_type) {
                message.type = rawMessage.system_message_context
                  .message_type as VoiceboxResponseMessageType;
              }
              if (typeof rawMessage.score === 'number') {
                message.feedbackScore = rawMessage.score;
              }
              if (rawMessage.system_message_context.followup_examples) {
                message.examples =
                  rawMessage.system_message_context.followup_examples.filter(
                    (example): example is string => Boolean(example)
                  );
              }

              acc.push(message);
            }

            return acc;
          },
          []
        );
        return { conversationId, messages };
      } catch (err) {
        console.error('Problem retrieving conversation', err);
        return { conversationId, messages: null };
      }
    },
    options
  );
